import React, { useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { fetchStoreBySlug } from '../store/reducers/homeReducer'
import Headers from '../components/Headers'
import { normalizeStoreName } from '../utils/storeUtils'
import Footer from '../components/Footer'

const TermsAndConditions = () => {
    const { storeName } = useParams()
    const isStoreMode = !!storeName;
    const dispatch = useDispatch()
    const { seller } = useSelector(state => state.home)

    // Theme logic - Only if NOT StoreMode
    const getThemeClass = () => {
        if (isStoreMode) return '';

        const themeSettings = seller?.themeSettings;
        const useCustomTheme = themeSettings?.useCustomTheme;

        if (useCustomTheme) {
            return 'light-theme';
        }
        if (seller && seller.shopInfo) {
            return seller.shopInfo.theme === 'light' ? 'light-theme' : '';
        }
        if (storeName) {
            const normalizedStore = normalizeStoreName(storeName);
            const cachedTheme = localStorage.getItem(`store-theme-${normalizedStore}`);
            return cachedTheme === 'light' ? 'light-theme' : '';
        }
        return '';
    };
    const themeClass = getThemeClass();

    // Apply theme class to body immediately on render if we have cached theme context
    // Only if NOT StoreMode
    if (!isStoreMode && themeClass === 'light-theme') {
        document.body.classList.add('light-theme');

        // If we have cached custom theme settings, apply them now to prevent flash
        if (storeName) {
            try {
                const normalizedStore = normalizeStoreName(storeName);
                const cachedSettings = localStorage.getItem(`store-settings-${normalizedStore}`);
                if (cachedSettings) {
                    const settings = JSON.parse(cachedSettings);
                    if (settings.useCustomTheme && settings.colors) {
                        const colors = settings.colors;
                        const setVar = (name, value) => {
                            if (value) document.body.style.setProperty(name, value);
                        };
                        const getContrastColor = (hexcolor) => {
                            if (!hexcolor || hexcolor === 'transparent') return null;
                            const hex = hexcolor.replace('#', '');
                            const r = parseInt(hex.slice(0, 2), 16);
                            const g = parseInt(hex.slice(2, 4), 16);
                            const b = parseInt(hex.slice(4, 6), 16);
                            const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
                            return (yiq >= 128) ? '#000000' : '#ffffff';
                        };
                        const contrastColor = getContrastColor(colors.background);
                        setVar('--store-bg', colors.background);
                        setVar('--store-text', contrastColor);
                        setVar('--store-icon', contrastColor);
                        setVar('--store-button-bg', colors.buttonBg);
                        setVar('--store-button-text', colors.buttonText);
                        setVar('--store-product-card-bg', colors.productCardBg);
                        setVar('--store-header-bg', colors.headerBg);
                        setVar('--store-footer-bg', colors.footerBg);
                        setVar('--store-border', colors.border);
                        if (settings.typography?.fontFamily) {
                            setVar('--store-font-family', `'${settings.typography.fontFamily}', sans-serif`);
                        }
                    }
                }
            } catch (e) {
                console.error("Error applying cached theme:", e);
            }
        }
    }

    useEffect(() => {
        // Only fetch store if NOT in StoreLayout. StoreLayout handles it otherwise.
        if (storeName && !isStoreMode) {
            dispatch(fetchStoreBySlug(storeName))
        }
    }, [storeName, dispatch, isStoreMode])

    // Theme effect - Only if NOT StoreMode
    useEffect(() => {
        if (isStoreMode) return;

        if (seller && seller.shopInfo && storeName) {
            const normalizedStore = normalizeStoreName(storeName);
            localStorage.setItem(`store-theme-${normalizedStore}`, seller.shopInfo.theme);
            localStorage.setItem(`store-settings-${normalizedStore}`, JSON.stringify(seller.themeSettings || {}));

            const themeSettings = seller?.themeSettings;
            const useCustomTheme = themeSettings?.useCustomTheme;

            // Apply light-theme class when using custom theme or when theme is 'light'
            if (useCustomTheme || seller.shopInfo.theme === 'light') {
                document.body.classList.add('light-theme');
            } else {
                document.body.classList.remove('light-theme');
            }

            // Apply custom CSS variables when useCustomTheme is true
            if (useCustomTheme && themeSettings) {
                const colors = themeSettings.colors || {};
                const setVar = (name, value) => {
                    if (value) document.body.style.setProperty(name, value);
                };

                const getContrastColor = (hexcolor) => {
                    if (!hexcolor || hexcolor === 'transparent') return null;
                    const hex = hexcolor.replace('#', '');
                    const r = parseInt(hex.slice(0, 2), 16);
                    const g = parseInt(hex.slice(2, 4), 16);
                    const b = parseInt(hex.slice(4, 6), 16);
                    const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
                    return (yiq >= 128) ? '#000000' : '#ffffff';
                };

                const contrastColor = getContrastColor(colors.background);
                setVar('--store-bg', colors.background);
                setVar('--store-text', contrastColor);
                setVar('--store-icon', contrastColor);
                setVar('--store-button-bg', colors.buttonBg);
                setVar('--store-button-text', colors.buttonText);
                setVar('--store-product-card-bg', colors.productCardBg);
                setVar('--store-header-bg', colors.headerBg);
                setVar('--store-footer-bg', colors.footerBg);
                setVar('--store-input-bg', themeSettings.inputs?.background || colors.inputBg);
                setVar('--store-input-text', themeSettings.inputs?.text || colors.inputText);
                setVar('--store-input-border', themeSettings.inputs?.border || colors.inputBorder);
                setVar('--store-input-focus', themeSettings.inputs?.focusRing || '#000000');
                setVar('--store-input-radius', `${themeSettings.inputs?.radius || 20}px`);
                setVar('--store-select-bg', themeSettings.selects?.background || colors.inputBg);
                setVar('--store-select-text', themeSettings.selects?.text || colors.inputText);
                setVar('--store-select-border', themeSettings.selects?.border || colors.inputBorder);
                setVar('--store-select-focus', themeSettings.selects?.focusRing || '#000000');
                setVar('--store-select-radius', `${themeSettings.selects?.radius || 20}px`);
                setVar('--store-border', colors.border);
                setVar('--store-muted', colors.muted);
                setVar('--store-glass', colors.glass);

                // Load and apply Google Font
                if (themeSettings.typography?.fontFamily) {
                    const fontFamily = themeSettings.typography.fontFamily;
                    const fontId = `google-font-${fontFamily.replace(/\s+/g, '-')}`;

                    if (!document.getElementById(fontId)) {
                        const link = document.createElement('link');
                        link.id = fontId;
                        link.rel = 'stylesheet';
                        link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(fontFamily)}:wght@300;400;500;600;700&display=swap`;
                        document.head.appendChild(link);
                    }

                    setVar('--store-font-family', `"${fontFamily}", sans-serif`);
                    document.body.style.fontFamily = `"${fontFamily}", sans-serif`;
                }
            }
        }
        return () => {
            document.body.classList.remove('light-theme');
        };
    }, [seller, storeName, isStoreMode]);

    const content = storeName && seller?.footerInfo?.termsAndConditions
        ? seller.footerInfo.termsAndConditions
        : null

    return (
        <div className={`min-h-screen w-full transition-colors duration-300 ${!isStoreMode ? themeClass : ''} bg-[var(--store-bg)] text-[var(--store-text)]`}>
            {!isStoreMode && <Headers storeName={storeName} />}
            <div className='w-[85%] md:w-[95%] mx-auto py-16 md:py-8'>
                <div className={`rounded-lg p-8 md:p-4 bg-[var(--store-card-bg)]`}>
                    <h1 className='text-3xl md:text-2xl font-bold mb-6 text-[var(--store-text)]'>
                        {storeName ? `Terms and Conditions - ${seller?.shopInfo?.shopName || storeName}` : 'Terms and Conditions'}
                    </h1>

                    <div className='prose max-w-none text-[var(--store-muted)]'>
                        {content ? (
                            <div className="whitespace-pre-wrap text-[var(--store-text)]">
                                {content}
                            </div>
                        ) : (
                            <>
                                <p className='mb-4 text-[var(--store-muted)]'>
                                    <strong>Last updated:</strong> {new Date().toLocaleDateString()}
                                </p>

                                <h2 className='text-xl font-semibold mb-4 text-[var(--store-text)]'>Acceptance of Terms</h2>
                                <p className='mb-4 text-[var(--store-muted)]'>
                                    By accessing and using Stores, you accept and agree to be bound by the terms and provision
                                    of this agreement. These Terms and Conditions apply to all users of the platform.
                                </p>

                                <h2 className='text-xl font-semibold mb-4 text-[var(--store-text)]'>Account Registration</h2>
                                <ul className='list-disc pl-6 mb-4 text-[var(--store-muted)]'>
                                    <li>You must be at least 18 years old to create an account</li>
                                    <li>You are responsible for maintaining the confidentiality of your account credentials</li>
                                    <li>You agree to provide accurate and complete information during registration</li>
                                    <li>You are responsible for all activities that occur under your account</li>
                                </ul>

                                <h2 className='text-xl font-semibold mb-4 text-[var(--store-text)]'>Use of the Platform</h2>
                                <p className='mb-4 text-[var(--store-muted)]'>
                                    You agree to use Stores only for lawful purposes and in accordance with these Terms.
                                    You may not use the platform to sell prohibited items, engage in fraudulent activities,
                                    or violate any applicable laws or regulations.
                                </p>

                                <h2 className='text-xl font-semibold mb-4 text-[var(--store-text)]'>Seller Obligations</h2>
                                <ul className='list-disc pl-6 mb-4 text-[var(--store-muted)]'>
                                    <li>Provide accurate product descriptions and images</li>
                                    <li>Fulfill orders promptly and as described</li>
                                    <li>Respond to customer inquiries in a timely manner</li>
                                    <li>Comply with all applicable laws and regulations</li>
                                    <li>Pay applicable fees and commissions</li>
                                </ul>

                                <h2 className='text-xl font-semibold mb-4 text-[var(--store-text)]'>Payment and Fees</h2>
                                <p className='mb-4 text-[var(--store-muted)]'>
                                    All prices are displayed in the applicable currency and include applicable taxes.
                                    Payment processing fees and seller commission rates are disclosed during the checkout process.
                                </p>

                                <h2 className='text-xl font-semibold mb-4 text-[var(--store-text)]'>Limitation of Liability</h2>
                                <p className='mb-4 text-[var(--store-muted)]'>
                                    Stores shall not be liable for any indirect, incidental, special, or consequential damages
                                    resulting from the use of our platform. Our liability is limited to the maximum extent permitted by law.
                                </p>

                                <h2 className='text-xl font-semibold mb-4 text-[var(--store-text)]'>Modifications to Terms</h2>
                                <p className='mb-4 text-[var(--store-muted)]'>
                                    We reserve the right to modify these Terms and Conditions at any time. Changes will be effective
                                    immediately upon posting on the platform. Your continued use constitutes acceptance of the modified terms.
                                </p>

                                <h2 className='text-xl font-semibold mb-4 text-[var(--store-text)]'>Contact Information</h2>
                                <p className='text-[var(--store-muted)]'>
                                    For questions regarding these Terms and Conditions, please contact us at support@mypursepay.com.
                                </p>
                            </>
                        )}
                    </div>
                </div>
            </div>
            {!isStoreMode && <Footer />}
        </div>
    )
}

export default TermsAndConditions
